import { useRef, useCallback, useEffect, RefObject, useState } from 'react';
import { useGamepadBindings } from 'utils/gamepad';
import { useFeatureFlagEnabled, FF } from 'modules/feature-flags';
import { useWebHIDBindings } from 'domains/reporter/WebHID/useWebHIDBindings';
import { GamepadActionId } from 'generated/graphql';
import {
  recorderEmitter,
  type RecorderDataAvailableEvent,
  type PCMRecorderDataAvailableEvent,
} from './useRecorder';
import { SlateMediaRecorder } from './SlateMediaRecorder';
import { DownsampleRawPCMMonoAudioRecorder } from './DownsampleRawPCMMonoAudioRecorderWorklet';
import {
  RawPCMMonoAudioRecorder,
  NVOQ_PREFERRED_AUDIO_CHUNK_TIME_MS,
} from './RawPCMMonoAudioRecorderWorklet';
import { WithApolloContext } from 'modules/Apollo/types';

type RecorderWithSampleRate = RawPCMMonoAudioRecorder | DownsampleRawPCMMonoAudioRecorder;
const recorderHasSampleRate = (
  recorder: RecorderWithSampleRate | SlateMediaRecorder | null
): recorder is RecorderWithSampleRate =>
  recorder != null && 'sampleRate' in recorder && typeof recorder.sampleRate === 'number';

type UseShouldSkipWebsocketCreationReturn = {
  /** Ref to determine if WebSocket creation should be skipped */
  shouldSkipWebsocketCreationRef: {
    current: boolean;
  };
  /**
   * Set the current ref value for whether to skip WebSocket creation
   * @param shouldSkip - Flag indicating whether to skip or not
   */
  setShouldSkipWebsocketCreation: (shouldSkip: boolean) => void;
  /** Ref to the element that will have user activity (mousedown, mouseup and keydown) listeners attached */
  userActivityElementRef: RefObject<HTMLDivElement>;
  sendModifyDictationMessage: () => void;
  getLocalSequence: () => number;
};

type UseShouldSkipWebsocketCreationArgs = WithApolloContext & {
  isRecording: boolean;
  getRecorder: () =>
    | null
    | RawPCMMonoAudioRecorder
    | DownsampleRawPCMMonoAudioRecorder
    | SlateMediaRecorder;
};

const MODIFY_DICTATION_GAMEPAD_ACTIONS = new Set<GamepadActionId>([
  GamepadActionId.Forward,
  GamepadActionId.Back,
  GamepadActionId.ExitField,
  GamepadActionId.DeleteForward,
  GamepadActionId.DeleteBackward,
  GamepadActionId.DeleteWordBackward,
  GamepadActionId.NewLine,
  GamepadActionId.EndOfLine,
  GamepadActionId.EndOfReport,
  GamepadActionId.Space,
  GamepadActionId.Undo,
]);

// Native vertical caret movement updates Slate selection after keydown.
const MODIFY_DICTATION_SELECTION_SETTLING_KEYS = new Set(['ArrowUp', 'ArrowDown']);

export const useShouldSkipWebsocketCreation = ({
  isRecording,
  getRecorder,
  context,
}: UseShouldSkipWebsocketCreationArgs): UseShouldSkipWebsocketCreationReturn => {
  const shouldSkipWebsocketCreationRef = useRef(false);
  const userActivityElementRef = useRef<HTMLDivElement | null>(null);
  const [isReporterModifyDictationEnabled] = useFeatureFlagEnabled(FF.REPORTER_MODIFY_DICTATION);
  const chunkTrackerRef = useRef<
    Map<
      string,
      {
        accumulated: number;
        lastDurationReported?: number;
      }
    >
  >(new Map());

  // Local sequence doesn't get reset - it keeps incrementing for the lifetime of the component
  const [localSequence, setLocalSequence] = useState<number>(0);
  const localSequenceRef = useRef<number>(0);

  const setShouldSkipWebsocketCreation = useCallback((shouldSkip: boolean) => {
    shouldSkipWebsocketCreationRef.current = shouldSkip;
  }, []);

  const sendModifyDictationMessage = useCallback(() => {
    if (!isReporterModifyDictationEnabled) {
      return;
    }

    const recorder = getRecorder();

    if (recorder == null || !isRecording) return;

    // Keep the current recorder/websocket alive when issuing modify payloads.
    // This is especially important for cross-window shortcut flows where no local keydown event
    // would otherwise set this flag before the selection changes.
    setShouldSkipWebsocketCreation(true);

    const id = recorder.id;

    // Calculate audioOffsetMs based on accumulated duration,
    // relying on the chunk tracker that updates on 'data_available' events
    const tracker = chunkTrackerRef.current.get(id);
    const accumulated = tracker?.accumulated ?? 0;
    const now = recorder.getDurationMillis();
    const tail = Math.max(0, now - accumulated);
    const audioOffsetMs = accumulated + Math.min(tail, NVOQ_PREFERRED_AUDIO_CHUNK_TIME_MS);

    const nextLocalSequence = localSequenceRef.current + 1;
    localSequenceRef.current = nextLocalSequence;

    recorderEmitter.emit('modify_dictation', {
      id,
      audioOffsetMs,
      localSequence: nextLocalSequence,
    });

    setLocalSequence(nextLocalSequence);
  }, [isReporterModifyDictationEnabled, getRecorder, isRecording, setShouldSkipWebsocketCreation]);

  const gamepadCallback = useCallback(
    (params: { action: GamepadActionId; pressed: boolean }) => {
      const { action, pressed } = params;
      if (pressed && MODIFY_DICTATION_GAMEPAD_ACTIONS.has(action))
        if (isReporterModifyDictationEnabled) {
          setShouldSkipWebsocketCreation(true);
          sendModifyDictationMessage();
        } else {
          setShouldSkipWebsocketCreation(false);
        }
    },
    [isReporterModifyDictationEnabled, sendModifyDictationMessage, setShouldSkipWebsocketCreation]
  );

  useGamepadBindings({ callback: gamepadCallback, context });
  useWebHIDBindings({ callback: gamepadCallback });

  useEffect(() => {
    // Track accumulated audio duration per recorder ID
    const handleDataAvailable = (
      event: RecorderDataAvailableEvent | PCMRecorderDataAvailableEvent
    ) => {
      const { id, duration, frameCount } = event;
      const recorder = getRecorder();

      if (recorder == null || !isRecording) return;

      const tracker = chunkTrackerRef.current;
      const previous = tracker.get(id);
      const previousAccumulated = previous?.accumulated ?? 0;

      const recorderMatches = recorder?.id === id;
      if (!recorderMatches || !recorderHasSampleRate(recorder)) {
        tracker.delete(id);
        // Recorder does not match or does not have sample rate info; cannot track
        return;
      }

      if (frameCount == null || frameCount <= 0) {
        // If frameCount is not provided, we cannot track accurately
        return;
      }

      const chunkDurationMs = (frameCount / recorder.sampleRate) * 1000;
      const accumulated = previousAccumulated + chunkDurationMs;

      tracker.set(id, {
        accumulated,
        lastDurationReported: duration ?? previous?.lastDurationReported,
      });
    };

    const handleStopRecording = () => {
      chunkTrackerRef.current.clear();
    };

    recorderEmitter.on('data_available', handleDataAvailable);
    recorderEmitter.on('stop_recording', handleStopRecording);

    return () => {
      recorderEmitter.off('data_available', handleDataAvailable);
      recorderEmitter.off('stop_recording', handleStopRecording);
    };
  }, [getRecorder, isRecording]);

  const pendingMouseModifyRef = useRef<boolean>(false);
  const pendingKeyModifyAnimationFramesRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    // only job is to help correctly clean up listeners
    let currentElementRef: HTMLDivElement | null = null;
    const pendingKeyModifyAnimationFrames = pendingKeyModifyAnimationFramesRef.current;

    const handleMouseDown = () => {
      if (!isReporterModifyDictationEnabled) {
        setShouldSkipWebsocketCreation(false);
        return;
      }

      setShouldSkipWebsocketCreation(true);
      pendingMouseModifyRef.current = true;
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isReporterModifyDictationEnabled) {
        setShouldSkipWebsocketCreation(true);
        if (MODIFY_DICTATION_SELECTION_SETTLING_KEYS.has(event.key)) {
          const animationFrameId = requestAnimationFrame(() => {
            pendingKeyModifyAnimationFrames.delete(animationFrameId);
            sendModifyDictationMessage();
          });
          pendingKeyModifyAnimationFrames.add(animationFrameId);
          return;
        }

        sendModifyDictationMessage();
      } else {
        setShouldSkipWebsocketCreation(false);
      }
    };

    const handleMouseUp = () => {
      if (!pendingMouseModifyRef.current) return;
      pendingMouseModifyRef.current = false;

      if (isReporterModifyDictationEnabled) {
        sendModifyDictationMessage();
      } else {
        setShouldSkipWebsocketCreation(false);
      }
    };

    // adds eventListeners to an element to track mousedown and keydown.
    const attachListenersIfElementExists = () => {
      const currentElement = userActivityElementRef.current;
      if (currentElement) {
        currentElement.addEventListener('mousedown', handleMouseDown);
        currentElement.addEventListener('mouseup', handleMouseUp);
        currentElement.addEventListener('keydown', handleKeyDown);
        clearInterval(intervalId);
        currentElementRef = currentElement;
      }
    };

    const intervalId = setInterval(attachListenersIfElementExists, 0);

    return () => {
      // Cleanup: remove the listeners (if they were added) and clear the interval
      if (currentElementRef) {
        currentElementRef.removeEventListener('mousedown', handleMouseDown);
        currentElementRef.removeEventListener('mouseup', handleMouseUp);
        currentElementRef.removeEventListener('keydown', handleKeyDown);
      }
      clearInterval(intervalId);
      pendingMouseModifyRef.current = false;
      pendingKeyModifyAnimationFrames.forEach((animationFrameId) =>
        cancelAnimationFrame(animationFrameId)
      );
      pendingKeyModifyAnimationFrames.clear();
    };
  }, [
    isReporterModifyDictationEnabled,
    sendModifyDictationMessage,
    setShouldSkipWebsocketCreation,
  ]);

  return {
    shouldSkipWebsocketCreationRef,
    setShouldSkipWebsocketCreation,
    userActivityElementRef,
    sendModifyDictationMessage,
    getLocalSequence: () => localSequence,
  };
};
